import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Settings, Save, Palette, Mic, Users as UsersIcon, Film, Image as ImageIcon,
  ChevronDown, ChevronRight, Eye,
} from "lucide-react";
import {
  getChannelSettings,
  putChannelSettings,
  listStylePresets,
  listVoices,
} from "@/lib/api";
import { listCharacters } from "@/lib/charactersApi";
import StylePreviewDialog from "@/components/StylePreviewDialog";
import VoiceSamplePlayButton from "@/components/VoiceSamplePlayButton";

/**
 * Per-channel settings panel — the defaults every new storyboard on
 * this channel inherits. Renders as a collapsible card ABOVE the
 * "// GPT-5.2 Idea Engine" section on the scoped Storyboards page.
 *
 * Props:
 *   handle:  the normalised channel handle (no leading @). Empty
 *            string / null = don't render (the caller renders the
 *            "Unassigned" bucket instead).
 *   channelLabel: human-readable channel name for the header.
 */
export default function ChannelSettingsPanel({ handle, channelLabel }) {
  // Default to OPEN — the settings are the primary reason users
  // land on the scoped channel view for the first time, so hiding
  // them behind a click adds friction. The preference is persisted
  // to localStorage so users who collapse it stay collapsed on
  // return visits.
  const storageKey = handle ? `ytm.channelSettings.open.${handle}` : "";
  const [open, setOpen] = useState(() => {
    if (!storageKey) return true;
    const saved = localStorage.getItem(storageKey);
    // Absence of a saved pref = default to open. Only respect an
    // explicit "0" (user manually collapsed on a prior visit).
    return saved !== "0";
  });
  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, open ? "1" : "0");
  }, [open, storageKey]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [presets, setPresets] = useState([]);
  // The /api/auto-video-voices endpoint returns
  // {builtin: [...], custom: [...], resemble: [...]} — keep the raw
  // shape and let the <select> render group by group.
  const [voices, setVoices] = useState({ builtin: [], custom: [], resemble: [] });
  const [characters, setCharacters] = useState([]);

  // Live editable state — separate from the loaded settings so
  // "unsaved changes" is detectable and Save is a single atomic
  // upsert (partial PUT of only the changed keys).
  const [stylePreset, setStylePreset] = useState("");
  const [postFormat, setPostFormat] = useState("video");
  const [voiceId, setVoiceId] = useState("");
  const [characterIds, setCharacterIds] = useState([]);

  // Style preview dialog — {key, label} of the preset being previewed.
  const [previewPreset, setPreviewPreset] = useState(null);

  // Snapshot of what's persisted, for the "unsaved changes" chip.
  const [loaded, setLoaded] = useState(null);

  const bootstrap = useCallback(async () => {
    if (!handle) return;
    setLoading(true);
    try {
      const [settings, presetList, voiceList, charList] = await Promise.all([
        getChannelSettings(handle),
        listStylePresets().catch(() => []),
        listVoices().catch(() => ({ builtin: [], custom: [], resemble: [] })),
        listCharacters().catch(() => []),
      ]);
      setPresets(presetList || []);
      setVoices({
        builtin: voiceList?.builtin || [],
        custom: voiceList?.custom || [],
        resemble: voiceList?.resemble || [],
      });
      setCharacters(charList || []);
      setStylePreset(settings?.style_preset || "");
      setPostFormat(settings?.post_format || "video");
      setVoiceId(settings?.default_voice_id || "");
      setCharacterIds(Array.isArray(settings?.character_ids) ? settings.character_ids : []);
      setLoaded(settings || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load channel settings");
    } finally {
      setLoading(false);
    }
  }, [handle]);

  // Load lazily — only when the panel is first expanded, so we don't
  // hammer the API for users who never open it.
  useEffect(() => {
    if (open && loaded === null && !loading) bootstrap();
  }, [open, loaded, loading, bootstrap]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    const a = new Set(loaded.character_ids || []);
    const b = new Set(characterIds || []);
    if (a.size !== b.size) return true;
    for (const x of a) if (!b.has(x)) return true;
    return (
      (loaded.style_preset || "") !== stylePreset ||
      (loaded.post_format || "video") !== postFormat ||
      (loaded.default_voice_id || "") !== voiceId
    );
  }, [loaded, stylePreset, postFormat, voiceId, characterIds]);

  const onSave = useCallback(async () => {
    if (!handle) return;
    setSaving(true);
    try {
      const fresh = await putChannelSettings(handle, {
        style_preset: stylePreset,
        post_format: postFormat,
        default_voice_id: voiceId,
        character_ids: characterIds,
      });
      setLoaded(fresh);
      toast.success("Channel settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save settings");
    } finally {
      setSaving(false);
    }
  }, [handle, stylePreset, postFormat, voiceId, characterIds]);

  const toggleCharacter = (cid) => {
    setCharacterIds((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid],
    );
  };

  if (!handle) return null;

  return (
    <section
      data-testid="channel-settings-panel"
      className="border border-zinc-800 rounded-sm bg-zinc-950/50 mb-6"
    >
      <button
        type="button"
        data-testid="channel-settings-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-yellow-400/5 transition-colors"
      >
        <Settings className="w-4 h-4 text-yellow-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
            // CHANNEL SETTINGS
          </div>
          <div className="font-display text-lg font-bold text-white leading-snug">
            Defaults for {channelLabel || "this channel"}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">
            Style, post format, narration voice, and default cast that every new
            storyboard on this channel inherits automatically.
          </div>
        </div>
        {dirty && !open && (
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber-400 border border-amber-400/40 rounded-sm px-2 py-0.5">
            unsaved
          </span>
        )}
        {open ? (
          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="border-t border-zinc-800 p-5 space-y-6" data-testid="channel-settings-body">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading settings…
            </div>
          ) : (
            <>
              {/* --- Graphic style --- */}
              <SectionHeading icon={Palette} title="Graphic style">
                Every scene image + thumbnail on new storyboards uses this style. Click the
                eye icon on a style to generate a sample image on this channel's topic.
              </SectionHeading>
              <div
                data-testid="channel-settings-style-grid"
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
              >
                <StyleTile
                  active={stylePreset === ""}
                  label="Auto (channel kit default)"
                  description="Inherit from the channel kit's original preset."
                  onClick={() => setStylePreset("")}
                  testid="channel-settings-style-auto"
                />
                {presets.map((p) => (
                  <StyleTile
                    key={p.key}
                    active={stylePreset === p.key}
                    label={p.label}
                    description={p.description}
                    onClick={() => setStylePreset(p.key)}
                    onPreview={() => setPreviewPreset({ key: p.key, label: p.label })}
                    testid={`channel-settings-style-${p.key}`}
                  />
                ))}
              </div>
              <StylePreviewDialog
                open={!!previewPreset}
                onClose={() => setPreviewPreset(null)}
                presetKey={previewPreset?.key}
                presetLabel={previewPreset?.label}
                channelHandle={handle}
              />

              {/* --- Post format --- */}
              <SectionHeading icon={Film} title="Post format">
                Video = per-scene preview MP4 (Kling/Seedance/etc). Stills = image-only
                slideshow with Ken-Burns pan-zoom. Stills mode skips video renders entirely.
              </SectionHeading>
              <div className="grid grid-cols-2 gap-2 max-w-md">
                <FormatTile
                  active={postFormat === "video"}
                  icon={Film}
                  label="Video"
                  description="Full pipeline. Scenes get preview MP4s."
                  onClick={() => setPostFormat("video")}
                  testid="channel-settings-format-video"
                />
                <FormatTile
                  active={postFormat === "stills"}
                  icon={ImageIcon}
                  label="Stills"
                  description="Slideshow — stills + narration only."
                  onClick={() => setPostFormat("stills")}
                  testid="channel-settings-format-stills"
                />
              </div>

              {/* --- Narration voice --- */}
              <SectionHeading icon={Mic} title="Narration voice">
                ElevenLabs voice used when new storyboards render voice-overs. Leave on
                Auto to let each storyboard pick its own.
              </SectionHeading>
              <div className="flex items-center gap-3 max-w-md">
                <select
                  data-testid="channel-settings-voice-select"
                  value={voiceId}
                  onChange={(e) => setVoiceId(e.target.value)}
                  className="flex-1 min-w-0 bg-black border border-zinc-800 hover:border-yellow-400/60 focus:border-yellow-400 text-sm text-zinc-100 font-mono px-3 py-2 rounded-sm focus:outline-none transition-colors"
                >
                  <option value="">Auto (let storyboard decide)</option>
                  {(() => {
                    const builtin = (voices?.builtin || []).filter(Boolean);
                    const custom = (voices?.custom || []).filter(Boolean);
                    const resemble = (voices?.resemble || []).filter(Boolean);
                    // Fallback option for a saved voice_id that isn't
                    // in the current /voices response (e.g. Resemble
                    // library hasn't loaded, or the user rotated
                    // their Resemble token). Keeps the saved value
                    // visible instead of silently reverting to Auto.
                    const known = new Set([
                      ...builtin.map((v) => v.voice_id),
                      ...custom.map((v) => v.voice_id),
                      ...resemble.map((v) => v.voice_id),
                    ]);
                    const orphan = voiceId && !known.has(voiceId) ? voiceId : null;
                    const orphanLabel = orphan?.startsWith("rsm:")
                      ? `Resemble · ${orphan.slice(4, 12)}`
                      : orphan;
                    return (
                      <>
                        {orphan && (
                          <option value={orphan}>
                            {`${orphanLabel} (saved)`}
                          </option>
                        )}
                        {builtin.length > 0 && (
                          <optgroup label="Built-in voices">
                            {builtin.map((v) => (
                              <option key={v.voice_id} value={v.voice_id}>
                                {v.label}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {custom.length > 0 && (
                          <optgroup label="My voices (ElevenLabs library)">
                            {custom.map((v) => (
                              <option key={v.voice_id} value={v.voice_id}>
                                {v.label}
                                {v.category ? ` · ${v.category}` : ""}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {resemble.length > 0 && (
                          <optgroup label="Resemble voices">
                            {resemble.map((v) => (
                              <option key={v.voice_id} value={v.voice_id}>
                                {v.label}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
                <VoiceSamplePlayButton
                  voiceId={voiceId}
                  size="md"
                  testid="channel-settings-voice-sample-play"
                />
              </div>

              {/* --- Default characters --- */}
              <SectionHeading icon={UsersIcon} title="Default cast">
                Characters auto-attached to every new storyboard on this channel. Add or
                edit characters in the{" "}
                <Link
                  to="/characters"
                  className="text-yellow-400 underline hover:text-yellow-300"
                >
                  Character Library
                </Link>
                .
              </SectionHeading>
              {characters.length === 0 ? (
                <div className="text-xs text-zinc-500 font-mono border border-dashed border-zinc-800 rounded-sm p-4">
                  No characters in your library yet.{" "}
                  <Link
                    to="/characters"
                    className="text-yellow-400 underline hover:text-yellow-300"
                  >
                    Create one →
                  </Link>
                </div>
              ) : (
                <div
                  data-testid="channel-settings-character-grid"
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
                >
                  {characters.map((c) => {
                    const on = characterIds.includes(c.character_id);
                    return (
                      <button
                        key={c.character_id}
                        type="button"
                        data-testid={`channel-settings-character-toggle-${c.character_id}`}
                        onClick={() => toggleCharacter(c.character_id)}
                        className={
                          on
                            ? "text-left border border-yellow-400 bg-yellow-400/10 rounded-sm p-2.5"
                            : "text-left border border-zinc-800 hover:border-zinc-600 bg-black rounded-sm p-2.5 transition-colors"
                        }
                      >
                        <div className="text-sm font-bold text-zinc-100 truncate">
                          {c.name}
                          {on && (
                            <span className="ml-1.5 font-mono text-[9px] text-yellow-400">
                              ✓ default
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 truncate">
                          {c.role || "—"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* --- Save --- */}
              <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
                <button
                  type="button"
                  data-testid="channel-settings-save-btn"
                  onClick={onSave}
                  disabled={!dirty || saving}
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-black px-4 py-2 rounded-sm disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  {saving ? "Saving…" : "Save channel defaults"}
                </button>
                {dirty && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">
                    unsaved changes
                  </span>
                )}
                {!dirty && loaded?.updated_at && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    saved · {new Date(loaded.updated_at).toLocaleString()}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function SectionHeading({ icon: Icon, title, children }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-yellow-400" />
        <div className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-zinc-100">
          {title}
        </div>
      </div>
      <div className="text-xs text-zinc-400 leading-relaxed">{children}</div>
    </div>
  );
}

function StyleTile({ active, label, description, onClick, onPreview, testid }) {
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className={
          active
            ? "w-full h-full text-left border border-yellow-400 bg-yellow-400/10 rounded-sm p-3"
            : "w-full h-full text-left border border-zinc-800 hover:border-zinc-600 bg-black rounded-sm p-3 transition-colors"
        }
      >
        <div className={onPreview ? "text-sm font-bold text-zinc-100 pr-6" : "text-sm font-bold text-zinc-100"}>
          {label}
        </div>
        <div className="font-mono text-[10px] text-zinc-500 leading-snug mt-1 line-clamp-2">
          {description}
        </div>
      </button>
      {onPreview && (
        <button
          type="button"
          data-testid={`${testid}-preview-btn`}
          title="Generate a sample image in this style"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-yellow-400 transition-colors"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function FormatTile({ active, icon: Icon, label, description, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={
        active
          ? "text-left border border-yellow-400 bg-yellow-400/10 rounded-sm p-3"
          : "text-left border border-zinc-800 hover:border-zinc-600 bg-black rounded-sm p-3 transition-colors"
      }
    >
      <div className="flex items-center gap-2">
        <Icon className={active ? "w-4 h-4 text-yellow-400" : "w-4 h-4 text-zinc-400"} />
        <div className="text-sm font-bold text-zinc-100">{label}</div>
      </div>
      <div className="font-mono text-[10px] text-zinc-500 leading-snug mt-1">
        {description}
      </div>
    </button>
  );
}
